import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import type { Product, ReviewListQuery } from '@klingerator/shared';
import { api } from '@/services/api';

export interface ReviewParams {
  q: string;
  sort: ReviewListQuery['sort'];
  order: 'asc' | 'desc';
  handled: boolean;
}

interface CacheEntry {
  pages: Map<number, Product[]>;
  total: number;
  fetchedAt: number;
}

const PAGE_SIZE = 30;
/** Pagina-cache per zoek-/sorteercombinatie; verlopen na 2 min zodat de server niet per scroll geraakt wordt. */
const CACHE_TTL_MS = 2 * 60 * 1000;
const COUNT_POLL_MS = 60 * 1000;

export const useReviewStore = defineStore('review', () => {
  // ---- badge (aantal open inzendingen) ----
  const openCount = ref(0);
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  async function fetchCount(): Promise<void> {
    try {
      const { data } = await api.get<{ open: number }>('/review/count', { silent: true, noToast: true });
      openCount.value = data.open;
    } catch {
      /* badge is best-effort */
    }
  }

  /** Start de 60s-poll voor de badge (idempotent); alleen aanroepen voor goedkeurders/admins. */
  function startPolling(): void {
    if (pollTimer) return;
    fetchCount();
    pollTimer = setInterval(fetchCount, COUNT_POLL_MS);
  }
  function stopPolling(): void {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
  }

  // ---- lijst met pagina-cache (infinite scroll) ----
  const params = reactive<ReviewParams>({ q: '', sort: 'submitted_at', order: 'desc', handled: false });
  const cache = new Map<string, CacheEntry>();
  const items = ref<Product[]>([]);
  const total = ref(0);
  const loading = ref(false);
  const loadedPages = ref(0);

  const cacheKey = () => JSON.stringify(params);
  const hasMore = computed(() => items.value.length < total.value);

  function rebuildFromCache(entry: CacheEntry): void {
    const all: Product[] = [];
    for (let i = 0; i < entry.pages.size; i++) all.push(...(entry.pages.get(i) ?? []));
    items.value = all;
    total.value = entry.total;
    loadedPages.value = entry.pages.size;
  }

  /** Laadt pagina `page` (0-based); uit de cache als die vers is. */
  async function fetchPage(page: number): Promise<void> {
    const key = cacheKey();
    let entry = cache.get(key);
    if (entry && Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
      cache.delete(key);
      entry = undefined;
    }
    if (entry?.pages.has(page)) {
      rebuildFromCache(entry);
      return;
    }
    loading.value = true;
    try {
      const { data } = await api.get<{ items: Product[]; total: number }>('/review/products', {
        params: {
          handled: params.handled,
          q: params.q || undefined,
          sort: params.sort,
          order: params.order,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
        silent: true,
      });
      if (!entry) {
        entry = { pages: new Map(), total: data.total, fetchedAt: Date.now() };
        cache.set(key, entry);
      }
      entry.pages.set(page, data.items);
      entry.total = data.total;
      rebuildFromCache(entry);
    } finally {
      loading.value = false;
    }
  }

  /** Eerste pagina (na wijziging van zoek/sortering/filter). */
  async function reload(): Promise<void> {
    await fetchPage(0);
  }

  async function loadMore(): Promise<void> {
    if (loading.value || !hasMore.value) return;
    await fetchPage(loadedPages.value);
  }

  /** Cache legen (na indienen/afkeuren/push) en badge verversen. */
  function invalidate(): void {
    cache.clear();
    fetchCount();
  }

  return {
    openCount,
    fetchCount,
    startPolling,
    stopPolling,
    params,
    items,
    total,
    loading,
    hasMore,
    fetchPage,
    reload,
    loadMore,
    invalidate,
  };
});
